"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Search, X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@/i18n/routing";
import { ljSuggest } from "@/fixtures/lj/search-fixtures";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

/**
 * LJ's search field, with type-ahead.
 *
 * Submits as a real form, so Return works and the query lands in the URL —
 * which is what makes a result page linkable. The suggestion list is new: the
 * field offered nothing until you pressed Return, so a reader had to guess a
 * spelling the magazine uses. QA: "add auto suggestion when added text in
 * search bar".
 *
 * The submit button's hover was a fade, which is the lightening LJ asked to be
 * rid of everywhere; it inverts to the ink now, the same move the chips and the
 * follow pill make.
 */
export function LjSearchBar({ initialQuery = "" }: { initialQuery?: string }) {
  const [value, setValue] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  const suggestions = useMemo(() => ljSuggest(value), [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const go = (q: string) => {
    const trimmed = q.trim();
    setOpen(false);
    setActive(-1);
    // The field keeps what was chosen. Picking a suggestion navigated but left
    // the box showing what had been TYPED, because this component is not
    // remounted by the navigation and `initialQuery` only seeds the first
    // render. QA: "after clicking on the auto suggestion item, the text should
    // show in the search bar".
    setValue(trimmed);
    router.push(
      trimmed ? `/lj/search?q=${encodeURIComponent(trimmed)}` : "/lj/search",
    );
  };

  const clear = () => {
    setValue("");
    setOpen(false);
    setActive(-1);
    inputRef.current?.focus();
    // Emptying the box is only half of it: the results below are rendered on
    // the server from `?q=`, so a cleared field sat above the previous result
    // set. QA: "when hit on the close icon button to clear search in search
    // bar, should clear the query param".
    if (searchParams.toString()) router.replace("/lj/search");
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    go(active >= 0 && suggestions[active] ? suggestions[active] : value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={wrapRef} className="relative mx-auto w-full max-w-[640px]">
      <form
        onSubmit={onSubmit}
        role="search"
        className="flex w-full items-stretch"
        style={{ borderBottom: `1px solid ${LJ_COLORS.textPrimary}` }}
      >
        <label className="sr-only" htmlFor="lj-search">
          Search
        </label>
        <input
          id="lj-search"
          ref={inputRef}
          type="search"
          // Surfshark (and similar extensions) stamp data-sharkid on visible inputs before React hydrates;
          // that attribute is never in the server HTML, so the mismatch is theirs, not ours.
          suppressHydrationWarning
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open && suggestions.length > 0}
          aria-controls="lj-search-suggestions"
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search Lifestyle Journal…"
          // `appearance-none` plus the `::-webkit-search-cancel-button` reset in
          // globals.css removes the ENGINE's clear widget — a tiny grey ✕ that
          // renders differently per browser and ignores the page's type. QA:
          // "the close icon button in the search bar is ugly". Ours is below.
          className="lj-search-input min-w-0 flex-1 appearance-none bg-transparent px-1 py-3 outline-none"
          style={{ ...LJ_TYPE.dek, color: LJ_COLORS.textPrimary }}
        />
        {value.length > 0 ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={clear}
            // No ring: the field is a single hairline rule, and a bordered
            // disc sitting on it read as a second control bolted to the end.
            // QA: "remove the border on the close icon button". The glyph
            // darkens to the ink on hover, which is what the submit button
            // beside it does.
            className="my-auto mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[color:var(--rest-fg)] transition-colors duration-150 hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
            style={
              {
                ["--rest-fg" as string]: LJ_COLORS.textSecondary,
                ["--hover-fg" as string]: LJ_COLORS.textPrimary,
              } as React.CSSProperties
            }
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        ) : null}
        {/* The hover used to flood the button black, so a dark rectangle
            appeared at the end of an otherwise hairline field — QA: "the
            current hover effect on the search button is weird". The glyph
            itself responds instead: grey at rest, ink on hover, which is how
            the rest of the field behaves. */}
        <button
          type="submit"
          aria-label="Search"
          className="inline-flex items-center justify-center px-3 text-[color:var(--rest-fg)] transition-colors duration-150 hover:text-[color:var(--hover-fg)] motion-reduce:transition-none"
          style={
            {
              ["--rest-fg" as string]: LJ_COLORS.textSecondary,
              ["--hover-fg" as string]: LJ_COLORS.textPrimary,
            } as React.CSSProperties
          }
        >
          <Search className="h-5 w-5" />
        </button>
      </form>

      {open && suggestions.length > 0 ? (
        <ul
          id="lj-search-suggestions"
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 m-0 flex list-none flex-col p-0"
          style={{
            backgroundColor: LJ_COLORS.bgPage,
            border: `1px solid ${LJ_COLORS.dividerHair}`,
          }}
        >
          {suggestions.map((s, i) => (
            <li key={s} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(s)}
                className="block w-full truncate px-4 py-3 text-left transition-colors duration-150 hover:bg-[color:var(--hover-bg)] motion-reduce:transition-none"
                style={
                  {
                    ...LJ_TYPE.body,
                    fontSize: 14,
                    color: LJ_COLORS.textPrimary,
                    backgroundColor:
                      i === active ? LJ_COLORS.bgSubtle : "transparent",
                    ["--hover-bg" as string]: LJ_COLORS.bgSubtle,
                  } as React.CSSProperties
                }
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
